import { BoxGeometry, Group, IcosahedronGeometry, Mesh, MeshLambertMaterial, TorusGeometry, type BufferGeometry, type Material } from 'three';
import { wallBoxes, type AttackObject } from '../../core/boss/attacks';
import { GROUND_Y, WORLD_TOP } from '../../data/tuning';

/**
 * The portrait walls of JEFE-2 in its final phase (GDD §9.2, issue #111): each wall is two boxes around a gap, the
 * boxes are exactly the AABB of the core, and the caricature of the portrait (big glasses, an executive's suit or a
 * coat) is drawn inside the larger piece without leaving its bounds, so the visual box equals the collision box.
 * A fixed pool of walls is reused every frame and disposed with the scene. Portraits are parody caricatures (§3.4).
 */
const POOL = 6;
const DEPTH = 1;
const COLORS = {
  'orejon-1': { frame: 0x8a8f96, suit: 0x3a4a6a },
  'orejon-2': { frame: 0x8a8f96, suit: 0x5a4a3a },
  chepe: { frame: 0x8a8f96, suit: 0xd8d4c4 },
} as const;
const FACE = 0xd9a37a;
const GLASSES = 0x2a2320;

export interface WallPool {
  group: Group;
  update(walls: readonly AttackObject[], tick: number): void;
  dispose(): void;
}

export function buildWallPool(): WallPool {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const group = new Group();
  group.name = 'boss-portrait-walls';
  const unit = new BoxGeometry(1, 1, DEPTH);
  geometries.push(unit);
  const head = new IcosahedronGeometry(0.5, 0);
  const glasses = new TorusGeometry(0.16, 0.035, 4, 8);
  geometries.push(head, glasses);
  const frames = { 'orejon-1': paint(COLORS['orejon-1'].frame), 'orejon-2': paint(COLORS['orejon-2'].frame), chepe: paint(COLORS.chepe.frame) };
  const suits = { 'orejon-1': paint(COLORS['orejon-1'].suit), 'orejon-2': paint(COLORS['orejon-2'].suit), chepe: paint(COLORS.chepe.suit) };
  const face = paint(FACE);
  const lens = paint(GLASSES);

  interface Piece {
    holder: Group;
    box: Mesh;
    portrait: Group;
  }
  const make = (): Piece => {
    const holder = new Group();
    const box = new Mesh(unit, frames['orejon-1']);
    // The caricature: a head with big glasses over a suit block, all inside the unit box (scaled with it).
    const portrait = new Group();
    const suit = new Mesh(unit, suits['orejon-1']);
    suit.scale.set(0.6, 0.35, 0.3);
    suit.position.set(0, -0.28, DEPTH / 2 - 0.2);
    const skull = new Mesh(head, face);
    skull.scale.set(0.5, 0.5, 0.3);
    skull.position.set(0, 0.12, DEPTH / 2 - 0.2);
    for (const x of [-0.12, 0.12]) {
      const ring = new Mesh(glasses, lens);
      ring.scale.set(0.5, 0.5, 0.5);
      ring.position.set(x, 0.14, DEPTH / 2 - 0.08);
      portrait.add(ring);
    }
    portrait.add(suit, skull);
    holder.add(box, portrait);
    holder.visible = false;
    group.add(holder);
    return { holder, box, portrait };
  };
  const pieces: Piece[][] = Array.from({ length: POOL }, () => [make(), make()]);

  return {
    group,
    update(walls, tick) {
      pieces.forEach((pair, index) => {
        const wall = walls[index];
        pair.forEach((piece, side) => (piece.holder.visible = wall !== undefined && side < 2));
        if (!wall) return;
        const portrait = wall.wall!.portrait as keyof typeof COLORS;
        const boxes = wallBoxes(wall, tick);
        const larger = boxes[0]!.height >= boxes[1]!.height ? 0 : 1;
        boxes.forEach((geometry, side) => {
          const piece = pair[side]!;
          const top = Math.min(geometry.y + geometry.height / 2, WORLD_TOP);
          const bottom = Math.max(geometry.y - geometry.height / 2, GROUND_Y);
          piece.holder.name = `boss:JEFE-2:portrait:${portrait}`;
          piece.holder.position.set(geometry.x, (top + bottom) / 2, 0);
          piece.box.scale.set(geometry.width, Math.max(top - bottom, 0.001), 1);
          piece.box.material = frames[portrait];
          // The caricature goes on the larger piece, as large as fits inside it.
          piece.portrait.visible = side === larger;
          const size = Math.min(geometry.width, top - bottom);
          piece.portrait.scale.set(size, size, 1);
          for (const child of piece.portrait.children) if (child instanceof Mesh && child.geometry === unit) child.material = suits[portrait];
        });
      });
    },
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
